// /keep-mowing — public list of opt-in continuation crowdfunds.
// Shows redacted street + city, the Mowkin's first name, bid amount,
// pledge progress, and a "view details" link to /keep-mowing/<id>.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface CrowdfundCard {
  id: number;
  bid_amount_cents: number;
  cycle_months: number;
  bid_notes: string | null;
  status: string;
  mowkin_label: string | null;
  mowkin_kind: 'volunteer' | 'provider' | null;
  resident_street: string;
  resident_city: string;
  resident_county: string;
  pledged_cents: number;
  pledger_count: number;
}

function fmtMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

export function KeepMowingPage() {
  const [funds, setFunds] = useState<CrowdfundCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/crowdfunds');
        const j = await res.json() as { ok: boolean; data?: { crowdfunds: CrowdfundCard[] }; error?: string };
        if (!j.ok) throw new Error(j.error || 'fetch failed');
        setFunds(j.data?.crowdfunds || []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Load failed');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
      <div className="text-xs tracking-[0.25em] uppercase text-rose mb-3">Keep Mowing</div>
      <h1
        className="text-5xl text-forest mb-3 leading-tight font-display"
        style={{ fontVariationSettings: '"opsz" 144' }}
      >
        Households needing continuation.
      </h1>
      <p className="text-forest/75 mb-8 max-w-2xl leading-relaxed">
        Each household below finished their free cycle and chose to keep
        going through the neighborhood.{' '}
        <Link to="/lifecycle" className="text-rose hover:underline">
          How this works →
        </Link>
      </p>

      {error && <div className="bg-rose/10 border-l-4 border-rose p-4 text-rose text-sm mb-6">{error}</div>}
      {loading ? (
        <div className="text-forest/60 italic">Loading...</div>
      ) : funds.length === 0 ? (
        <div className="bg-cream border border-forest/15 p-8">
          <p className="text-forest/75 leading-relaxed">
            No households are currently listed. When a free cycle ends and
            a household opts in, they'll show up here. Keep checking back.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {funds.map((f) => {
            const target = f.bid_amount_cents * f.cycle_months;
            const pct = Math.min(100, Math.round((f.pledged_cents / target) * 100));
            return (
              <Link
                key={f.id}
                to={`/keep-mowing/${f.id}`}
                className="block bg-cream border border-forest/15 hover:border-rose transition-colors p-5"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs tracking-[0.15em] uppercase text-forest/55">
                    {f.resident_city}, {f.resident_county}
                  </div>
                  <span className="text-[10px] tracking-[0.15em] uppercase bg-forest/10 text-forest/70 px-2 py-0.5">
                    {f.status}
                  </span>
                </div>
                <h2
                  className="text-2xl text-forest font-display mb-2 leading-tight"
                  style={{ fontVariationSettings: '"opsz" 144' }}
                >
                  A household on {f.resident_street}
                </h2>
                {f.mowkin_label && (
                  <p className="text-sm text-forest/70 mb-3">
                    Continuing care from <strong>{f.mowkin_label}</strong>
                  </p>
                )}
                <div className="bg-forest/5 h-2 mb-2 overflow-hidden">
                  <div
                    className="bg-rose h-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-sm text-forest/80">
                  <span>
                    <strong className="text-forest">{fmtMoney(f.pledged_cents)}</strong>
                    <span className="text-forest/55"> of {fmtMoney(target)}</span>
                  </span>
                  <span className="text-forest/55">
                    {f.pledger_count} pledge{f.pledger_count === 1 ? '' : 's'}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
